import React from "react";
import { Section } from "../components/SectionTitle";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import SEO from "../components/SEO";
import { ShieldCheck, Heart, TrendingUp, Eye, Star } from "lucide-react";

const VALUES = [
  { icon: Eye, label: "Transparency", desc: "Clear communication and honest guidance at every step." },
  { icon: ShieldCheck, label: "Trust", desc: "Confidential, respectful, and reliable service." },
  { icon: Heart, label: "Protection", desc: "Designed around safeguarding what matters most." },
  { icon: TrendingUp, label: "Growth", desc: "Plans that adapt with your life and business." },
  { icon: Star, label: "Professionalism", desc: "Coordinated work with qualified professionals." },
  { icon: ShieldCheck, label: "Client-first guidance", desc: "Your goals shape every recommendation." },
];

export default function About() {
  return (
    <div>
      <SEO
        title="About HSA Service Hub"
        description="HSA Service Hub coordinates financial, tax, insurance, and legal services under one trusted hub for individuals, families, and businesses across the United States."
        keywords="HSA Service Hub about, financial advisory company USA, tax and insurance services hub"
        path="/about"
      />
      <section className="bg-[#0a2540] text-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 max-w-4xl">
          <div className="eyebrow text-[#d4af37] mb-3">About HSA Service Hub</div>
          <h1 className="font-serif-hsa text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] mb-5">
            One umbrella. Endless possibilities for the people we serve.
          </h1>
          <p className="text-lg text-white/85 max-w-2xl">
            HSA Service Hub was created to provide individuals, families, and businesses with access to
            essential support in financial planning, tax preparation, insurance protection, and legal
            service coordination.
          </p>
        </div>
      </section>

      <Section className="bg-white">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="eyebrow mb-3">Our Story</div>
            <h2 className="font-serif-hsa text-3xl sm:text-4xl font-semibold text-[#0a2540] leading-tight">
              Built on transparency, trust, and long-term protection
            </h2>
          </div>
          <div className="lg:col-span-7 text-slate-700 leading-relaxed text-base space-y-5">
            <p>
              Most American households work with a different professional for every part of their
              financial life. A tax preparer here, an insurance agent there, a separate financial
              planner, and a lawyer for the documents that matter. The pieces rarely talk to each other.
            </p>
            <p>
              HSA Service Hub was created to change that. We coordinate financial, tax, insurance,
              and legal services under one trusted hub - so the parts of your plan finally work together.
              Our brand is built on transparency, trust, long-term protection, and client growth.
            </p>
            <p>
              Whether you are an individual organizing your finances for the first time, a family
              planning long-term protection, or a business owner balancing personal and business
              responsibilities, our team is here to help you make confident decisions.
            </p>
          </div>
        </div>
      </Section>

      <Section className="bg-[#f8f9fa]">
        <div className="grid lg:grid-cols-2 gap-10">
          <div className="bg-white border border-[#cbd5e1] rounded-2xl p-10">
            <div className="eyebrow mb-3">Our Mission</div>
            <h3 className="font-serif-hsa text-2xl sm:text-3xl font-semibold text-[#0a2540] leading-tight">
              "To help individuals, families, and businesses make confident decisions through
              transparent, comprehensive, and long-term focused professional service support."
            </h3>
          </div>
          <div className="bg-[#0a2540] text-white rounded-2xl p-10">
            <div className="eyebrow text-[#d4af37] mb-3">Our Philosophy</div>
            <h3 className="font-serif-hsa text-2xl sm:text-3xl font-semibold leading-tight mb-4">
              Coordinated service. Personal attention. Lasting protection.
            </h3>
            <p className="text-white/80 leading-relaxed">
              We believe the best decisions come from clear information, professional coordination,
              and a partner who listens before recommending.
            </p>
          </div>
        </div>
      </Section>

      <Section className="bg-white">
        <div className="eyebrow text-center mb-3">Our Values</div>
        <h2 className="font-serif-hsa text-3xl sm:text-4xl font-semibold text-[#0a2540] text-center mb-14">
          The principles that guide every conversation
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {VALUES.map(({ icon: Icon, label, desc }) => (
            <div key={label} className="lift-card border border-[#cbd5e1] rounded-xl p-6 bg-white">
              <div className="w-11 h-11 rounded-lg bg-[#1e6dd8]/10 flex items-center justify-center text-[#1e6dd8] mb-4">
                <Icon className="w-5 h-5" />
              </div>
              <h4 className="font-serif-hsa text-lg font-semibold text-[#0a2540] mb-2">{label}</h4>
              <p className="text-sm text-slate-600 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </Section>

      <section className="bg-[#0a2540] text-white py-20">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-serif-hsa text-3xl sm:text-4xl font-semibold mb-5">
            Talk with an HSA Service Hub advisor
          </h2>
          <p className="text-white/85 mb-8">
            Tell us about your goals. We will help you understand where to begin.
          </p>
          <Link to="/contact">
            <Button data-testid="about-cta-btn" className="bg-[#d4af37] hover:bg-[#b8951f] text-[#0a2540] font-semibold rounded-full px-7 h-12">
              Book a Consultation
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
